import { useState } from 'react';

import { type PendingUnlink } from '@/components/drawdowns/payments/investorPayments';
import { PaymentDetailsSection } from '@/components/drawdowns/payments/PaymentDetailsSection';
import { UnlinkPaymentDialog } from '@/components/drawdowns/payments/UnlinkPaymentDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useRemoveDrawdownPayment } from '@/hooks/react-query/useDrawdowns';
import type { DrawdownPayment } from '@/types/drawdown';

type InvestorPaymentsDialogProps = {
  open: boolean;
  onClose: () => void;
  fundId: string;
  drawdownId: string;
  investorName: string;
  payments: DrawdownPayment[];
  /** The call's currency, so a payment made in another one is flagged. */
  currency: string;
  canUnlink: boolean;
};

/**
 * The bank transactions credited to one investor on this call, and the way
 * off them: a matched line returns to the inbox, a hand entry goes for good.
 */
export function InvestorPaymentsDialog({
  open,
  onClose,
  fundId,
  drawdownId,
  investorName,
  payments,
  currency,
  canUnlink,
}: InvestorPaymentsDialogProps) {
  const removePayment = useRemoveDrawdownPayment();
  const [pendingUnlink, setPendingUnlink] = useState<PendingUnlink | null>(
    null
  );

  const handleConfirmUnlink = () => {
    if (!pendingUnlink) return;
    removePayment.mutate({
      fundId,
      drawdownId,
      paymentId: pendingUnlink.payment.id,
    });
    setPendingUnlink(null);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bank transactions - {investorName}</DialogTitle>
          <DialogDescription>
            The money received against this investor&apos;s share of the call.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3" data-testid="investor-payments">
          {payments.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No bank confirmation yet.
            </p>
          ) : (
            payments.map((payment, index) => (
              <PaymentDetailsSection
                key={payment.id}
                payment={payment}
                investorName={investorName}
                currency={currency}
                fundId={fundId}
                canEdit={canUnlink}
                isRemoving={removePayment.isPending}
                defaultOpen={index === 0}
                onUnlink={setPendingUnlink}
              />
            ))
          )}
        </div>
      </DialogContent>

      <UnlinkPaymentDialog
        pending={pendingUnlink}
        onCancel={() => setPendingUnlink(null)}
        onConfirm={handleConfirmUnlink}
      />
    </Dialog>
  );
}
